import { Link } from "react-router-dom";
import { Check, Minus, Gift, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";

const PLANS = [
  { id: "starter", name: "Starter", desc: "Petites entreprises", price: 69, users: "Jusqu'à 3 utilisateurs",
    items: ["Devis & calcul de volume m³", "CRM clients", "Planning des déménagements", "Équipes & véhicules", "Facturation & envoi par e-mail", "Signature électronique des devis"] },
  { id: "pro", name: "Pro", desc: "Entreprises structurées", price: 119, users: "Jusqu'à 10 utilisateurs", popular: true,
    items: ["Tout Starter inclus", "Application Nomade tablette, même hors-ligne", "Itinéraire du jour & temps de trajet", "Client prévenu de l'heure d'arrivée", "Factur-X & Plateforme Agréée 2026", "Export comptable FEC, Sage, EBP, Quadra"] },
  { id: "premium", name: "Premium", desc: "Multi-agences et IA", price: 199, users: "Utilisateurs illimités",
    items: ["Tout Pro inclus", "Estimation du volume par IA", "Assistant commercial IA", "Itinéraire optimisé automatiquement", "Idéal multi-agences"] },
];

const ROWS = [
  ["Devis, calcul de volume m³ & signature en ligne", 1, 1, 1],
  ["CRM clients & planning", 1, 1, 1],
  ["Équipes, véhicules & entretien", 1, 1, 1],
  ["Facturation & envoi des factures par e-mail", 1, 1, 1],
  ["Application Nomade (photos, signatures, comptes rendus, dépenses, hors-ligne)", 0, 1, 1],
  ["Itinéraire du jour avec temps de trajet", 0, 1, 1],
  ["Client prévenu de l'heure d'arrivée (e-mail / SMS)", 0, 1, 1],
  ["Facture électronique Factur-X & Plateforme Agréée", 0, 1, 1],
  ["Export comptable (FEC, Sage, EBP, Quadra, Excel, PDF)", 0, 1, 1],
  ["Estimation du volume par IA", 0, 0, 1],
  ["Assistant commercial IA", 0, 0, 1],
  ["Ordre des chantiers optimisé", 0, 0, 1],
  ["Utilisateurs", "3", "10", "Illimités"],
  ["Tablette Nomade 7\" offerte", 1, 1, 1],
];

const FAQ = [
  ["Y a-t-il un engagement ?", "Non. L'abonnement est mensuel et sans engagement : vous pouvez résilier à tout moment depuis votre espace Abonnement."],
  ["Comment fonctionne l'essai gratuit ?", "Vous profitez de 14 jours avec toutes les fonctions Premium, sans carte bancaire. À la fin de l'essai, vous choisissez la formule qui vous convient."],
  ["Puis-je changer de formule ?", "Oui, à tout moment. Le changement est immédiat et la différence est calculée au prorata sur votre facture suivante."],
  ["Comment résilier ?", "En un clic depuis Abonnement → « Gérer mon paiement / résilier ». Votre accès reste actif jusqu'à la fin de la période payée."],
  ["Les prix sont-ils HT ?", "Oui, les prix sont indiqués hors taxes, par mois et par entreprise. Le paiement est sécurisé par carte via Stripe."],
];

const Cell = ({ v }) => (typeof v === "string" ? <span className="text-sm font-semibold text-slate-900">{v}</span>
  : v ? <Check className="mx-auto h-5 w-5 text-teal-600" /> : <Minus className="mx-auto h-4 w-4 text-slate-300" />);

function PlanCard({ p }) {
  return (
    <div data-testid={`pricing-${p.id}`} className={`relative flex flex-col rounded-2xl border bg-white p-8 transition-transform duration-300 hover:-translate-y-1 ${p.popular ? "border-[#1559d6] shadow-xl shadow-blue-900/10" : "border-slate-200"}`}>
      {p.popular && <span className="absolute -top-3 left-8 flex items-center gap-1 rounded-full bg-[#1559d6] px-3 py-1 text-xs font-bold text-white" data-testid="pricing-popular-badge"><Sparkles className="h-3 w-3" />La plus choisie</span>}
      <h3 className="text-xl font-semibold uppercase tracking-wide text-slate-900">{p.name}</h3>
      <p className="mt-1 text-sm text-slate-500">{p.desc}</p>
      <p className="mt-6 font-display text-3xl font-bold text-[#0b2c6b]" data-testid={`pricing-${p.id}-price`}>{p.price} €<span className="text-base font-medium text-slate-400"> HT/mois</span></p>
      <p className="mt-1 text-xs font-semibold text-teal-700">{p.users}</p>
      <ul className="mt-6 flex-1 space-y-2.5 text-sm text-slate-700">
        {p.items.map((f) => <li key={f} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-teal-600" />{f}</li>)}
      </ul>
      <Button asChild className={`mt-8 w-full rounded-full ${p.popular ? "bg-[#1559d6] hover:bg-[#0b2c6b]" : "bg-[#0b2c6b] hover:bg-[#1559d6]"}`}>
        <Link to="/inscription" data-testid={`pricing-${p.id}-cta`}>Démarrer l'essai gratuit</Link>
      </Button>
    </div>
  );
}

function CompareTable() {
  return (
    <div className="mt-16 overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="pricing-compare-table">
      <table className="w-full min-w-[640px] text-left">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50">
            <th className="p-4 text-sm font-semibold text-slate-500">Comparer les formules</th>
            {PLANS.map((p) => <th key={p.id} className={`w-32 p-4 text-center text-sm font-bold ${p.popular ? "text-[#1559d6]" : "text-slate-900"}`}>{p.name}<span className="block text-xs font-medium text-slate-400">{p.price} € HT</span></th>)}
          </tr>
        </thead>
        <tbody>
          {ROWS.map(([label, ...vals], i) => (
            <tr key={label} className="border-b border-slate-100 last:border-0" data-testid={`compare-row-${i}`}>
              <td className="p-4 text-sm text-slate-700">{label}</td>
              {vals.map((v, j) => <td key={j} className={`p-4 text-center ${j === 1 ? "bg-blue-50/40" : ""}`}><Cell v={v} /></td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export const PricingSection = () => (
  <section id="tarifs" className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
    <h2 className="text-3xl font-bold text-slate-900 sm:text-4xl">Des formules adaptées à votre taille</h2>
    <div className="mt-4 flex flex-wrap gap-2">
      <p className="inline-flex items-center gap-2 rounded-full bg-[#0b2c6b] px-3 py-1 text-sm font-semibold text-white" data-testid="pricing-trial-badge"><Sparkles className="h-4 w-4" />14 jours d'essai Premium gratuits, sans carte bancaire</p>
      <p className="inline-flex items-center gap-2 rounded-full bg-teal-50 px-3 py-1 text-sm font-semibold text-teal-700"><Gift className="h-4 w-4" />Tablette Nomade 7" offerte avec chaque nouvel abonnement</p>
    </div>
    <div className="mt-12 grid gap-6 md:grid-cols-3">{PLANS.map((p) => <PlanCard key={p.id} p={p} />)}</div>
    <CompareTable />
    <div className="mx-auto mt-16 max-w-3xl" data-testid="pricing-faq">
      <h3 className="text-2xl font-bold text-slate-900">Questions fréquentes</h3>
      <Accordion type="single" collapsible className="mt-4">
        {FAQ.map(([q, a], i) => (
          <AccordionItem key={q} value={`q${i}`}>
            <AccordionTrigger data-testid={`faq-question-${i}`} className="text-left">{q}</AccordionTrigger>
            <AccordionContent className="text-slate-600">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);
